/**
 * Sticky Header Refinement
 * Optimized sticky header behavior with scroll detection and performance improvements
 * 
 * Features:
 * - Smooth scroll direction detection
 * - Intelligent show/hide based on scroll direction
 * - Performance optimized with RAF and throttling
 * - Accessibility support
 * - Mobile-friendly
 */

class StickyHeaderRefinement {
  constructor(headerElement, options = {}) {
    this.header = headerElement;
    this.options = {
      stickyClass: 'is-sticky',
      hiddenClass: 'is-hidden',
      scrollThreshold: 50,
      scrollUpThreshold: 10,
      enableScrollUpDetection: true,
      ...options
    };

    this.state = {
      isSticky: false,
      isHidden: false,
      lastScrollY: 0,
      scrollDirection: 'down',
      ticking: false,
      headerHeight: 0
    };

    this.init();
  }

  init() {
    if (!this.header) return;

    // Get header height
    this.state.headerHeight = this.header.offsetHeight;

    // Initial state
    this.state.lastScrollY = window.scrollY;

    // Listen to scroll events with throttling
    window.addEventListener('scroll', () => this.handleScroll(), { passive: true });

    // Handle window resize to update header height
    window.addEventListener('resize', () => {
      this.state.headerHeight = this.header.offsetHeight;
    });

    // Initial check
    this.updateStickyState();
  }

  handleScroll() {
    if (!this.state.ticking) {
      window.requestAnimationFrame(() => this.updateStickyState());
      this.state.ticking = true;
    }
  }

  updateStickyState() {
    const currentScrollY = window.scrollY;
    const scrollDelta = currentScrollY - this.state.lastScrollY;

    // Determine scroll direction
    if (scrollDelta > this.options.scrollUpThreshold) {
      this.state.scrollDirection = 'down';
    } else if (scrollDelta < -this.options.scrollUpThreshold) {
      this.state.scrollDirection = 'up';
    }

    // Determine if header should be sticky
    const shouldBeSticky = currentScrollY > this.options.scrollThreshold;

    // Determine if header should be hidden (when scrolling down)
    let shouldBeHidden = false;
    if (this.options.enableScrollUpDetection && shouldBeSticky) {
      shouldBeHidden = this.state.scrollDirection === 'down' && scrollDelta > 0;
    }

    // Update sticky state
    if (shouldBeSticky !== this.state.isSticky) {
      this.state.isSticky = shouldBeSticky;
      this.header.classList.toggle(this.options.stickyClass, shouldBeSticky);
      this.updateHeaderPosition();
    }

    // Update hidden state
    if (shouldBeHidden !== this.state.isHidden) {
      this.state.isHidden = shouldBeHidden;
      this.header.classList.toggle(this.options.hiddenClass, shouldBeHidden);
    }

    this.state.lastScrollY = currentScrollY;
    this.state.ticking = false;
  }

  updateHeaderPosition() {
    if (this.state.isSticky) {
      // Set CSS variable for other elements to reference header height
      document.documentElement.style.setProperty(
        '--sticky-header-height',
        `${this.state.headerHeight}px`
      );
    }
  }

  // Public methods for external control
  show() {
    this.header.classList.remove(this.options.hiddenClass);
    this.state.isHidden = false;
  }

  hide() {
    this.header.classList.add(this.options.hiddenClass);
    this.state.isHidden = true;
  }

  toggleSticky(enable) {
    if (enable) {
      this.header.classList.add(this.options.stickyClass);
      this.state.isSticky = true;
    } else {
      this.header.classList.remove(this.options.stickyClass);
      this.state.isSticky = false;
    }
    this.updateHeaderPosition();
  }

  destroy() {
    window.removeEventListener('scroll', () => this.handleScroll());
    window.removeEventListener('resize', () => {
      this.state.headerHeight = this.header.offsetHeight;
    });
  }
}

// Auto-initialize if data attribute is present
document.addEventListener('DOMContentLoaded', () => {
  const stickyHeaders = document.querySelectorAll('[data-sticky-header]');
  stickyHeaders.forEach(header => {
    const options = {
      stickyClass: header.dataset.stickyClass || 'is-sticky',
      hiddenClass: header.dataset.hiddenClass || 'is-hidden',
      scrollThreshold: parseInt(header.dataset.scrollThreshold) || 50,
      scrollUpThreshold: parseInt(header.dataset.scrollUpThreshold) || 10,
      enableScrollUpDetection: header.dataset.enableScrollUpDetection !== 'false'
    };
    new StickyHeaderRefinement(header, options);
  });
});

// Export for use as module
if (typeof module !== 'undefined' && module.exports) {
  module.exports = StickyHeaderRefinement;
}

window.StickyHeaderRefinement = StickyHeaderRefinement;
